import { Delete as DeleteIcon, Edit as EditIcon, Launch as LaunchIcon } from "@mui/icons-material";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import LocalFilePicker from "../../components/associatedFiles/LocalFilePicker";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import DatePicker from "../../components/datePicker/DatePicker";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import CostExchangeRate from "../CostExchangeRate";
import type {
  AddInvoiceRequest,
  CostCurrency,
  CostInvoiceStatus,
  CostResponse,
  InvoiceMagicoTwin,
  InvoiceResponse,
} from "../costApi";
import { InvoiceAttachedToManyCostsError } from "../costApi";
import Margin from "./Margin";
import InvoiceStatusSelect, { invoiceStatusToDisplayName } from "./InvoiceStatusSelect";
import { storageUrlWithCredentials } from "../../components/imagesPreview/storageUrlWithCredentials";
import InvoiceStatusDialog from "./InvoiceStatusDialog";
import { PdfExtractDialog } from "./PdfExtractDialog";
import { invoiceDateToExchangeRateDate } from "./InvoiceDateToExchangeRateDate";
import { useOpenInvoicePdf } from "../../sharedComponents/invoices/useOpenInvoicePdf";
import { useDragAndDrop } from "../../sharedComponents/useDragAndDrop";
import { useCustomerWithId } from "../../customers/useCustomerWithId";
import { useProducts } from "../../admin/products/useProducts";

interface CostInvoice {
  readonly invoiceNumber: string;
  readonly amount?: number;
  readonly date?: string;
  readonly url?: string;
  readonly exchangeRate?: number;
  readonly status: CostInvoiceStatus;
  readonly magico: InvoiceMagicoTwin | null;
}

interface MagicoInvoiceButtonProps {
  readonly twin: InvoiceMagicoTwin;
}

const MagicoInvoiceButton: FC<MagicoInvoiceButtonProps> = ({ twin }) => {
  const openInvoicePdf = useOpenInvoicePdf();
  return (
    <Tooltip title={`Faktura w Magico: ${twin.number}`}>
      <IconButton aria-label="Zobacz fakturę w Magico" color="info" onClick={() => openInvoicePdf(twin.pdfUrl)}>
        <LaunchIcon />
      </IconButton>
    </Tooltip>
  );
};

interface InvoiceRowProps {
  readonly invoice: CostInvoice;
  readonly costCurrency: CostCurrency;
  readonly showExchangeRate: boolean;
  onUpdateStatus: () => void;
  onDelete: () => void;
}

const InvoiceRow: FC<InvoiceRowProps> = ({ invoice, costCurrency, showExchangeRate, onUpdateStatus, onDelete }) => {
  const openInvoice = async () => {
    if (invoice.url?.includes("fakturownia")) {
      window.open(invoice.url, "_blank");
    } else {
      const signedUrl = await storageUrlWithCredentials(invoice.url);
      window.open(signedUrl, "_blank");
    }
  };
  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <IconButton aria-label="Zobacz fakturę" onClick={() => openInvoice()}>
            <LaunchIcon />
          </IconButton>
          {invoice.magico && <MagicoInvoiceButton twin={invoice.magico} />}
        </Stack>
      </TableCell>
      <TableCell aria-label="Numer faktury">{invoice.invoiceNumber}</TableCell>
      <TableCell aria-label="Data faktury">{invoice.date}</TableCell>
      <TableCell aria-label="Status">
        {invoiceStatusToDisplayName(invoice.status)}
        <IconButton onClick={onUpdateStatus} aria-label="Zmień status faktury">
          <EditIcon />
        </IconButton>
      </TableCell>
      <TableCell aria-label="Kwota">
        {invoice.amount} {costCurrency}
      </TableCell>
      {showExchangeRate && <TableCell aria-label="Kurs wymiany">{invoice.exchangeRate}</TableCell>}
      <TableCell>
        <IconButton edge="end" aria-label="Usuń fakturę" onClick={onDelete}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

interface InvoicesProps {
  readonly invoices: CostInvoice[];
  readonly costCurrency: CostCurrency;
  readonly orderCurrency: Currency;
  onUpdateStatus: (invoiceNumber: string, status: CostInvoiceStatus) => Promise<void>;
  onDelete: (invoiceNumber: string, deleteFromAll: boolean) => Promise<void>;
}

const Invoices: FC<InvoicesProps> = ({ invoices, costCurrency, orderCurrency, onUpdateStatus, onDelete }) => {
  const [invoiceConfirmationDialog, setInvoiceConfirmationDialog] = useState<string | null>(null);
  const [invoiceStatusDialog, setInvoiceStatusDialog] = useState<string | null>(null);
  const [invoiceDeleteAllDialog, setInvoiceDeleteAllDialog] = useState<string | null>(null);

  const handleDelete = async (invoiceNumber: string) => {
    try {
      await onDelete(invoiceNumber, false);
      setInvoiceConfirmationDialog(null);
    } catch (error) {
      if (error instanceof InvoiceAttachedToManyCostsError) {
        const invoiceNumberFromDialog = invoiceConfirmationDialog;
        setInvoiceConfirmationDialog(null);
        setInvoiceDeleteAllDialog(invoiceNumberFromDialog);
      } else {
        throw error;
      }
    }
  };

  const handleDeleteAll = async (invoiceNumber: string) => {
    await onDelete(invoiceNumber, true);
    setInvoiceDeleteAllDialog(null);
  };

  if (invoices.length === 0) {
    return null;
  }
  const showExchangeRate = costCurrency && costCurrency !== orderCurrency;

  return (
    <>
      <Table data-testid="cost-invoices">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Numer faktury</TableCell>
            <TableCell>Data</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Kwota</TableCell>
            {showExchangeRate && <TableCell>Kurs wymiany</TableCell>}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((i) => (
            <InvoiceRow
              key={i.invoiceNumber}
              invoice={i}
              costCurrency={costCurrency}
              showExchangeRate={showExchangeRate}
              onUpdateStatus={() => setInvoiceStatusDialog(i.invoiceNumber)}
              onDelete={() => setInvoiceConfirmationDialog(i.invoiceNumber)}
            />
          ))}
        </TableBody>
      </Table>
      <ConfirmationDialog
        open={!!invoiceConfirmationDialog}
        title="Usuń fakturę"
        content="Czy chcesz usunąć fakturę? Tej operacji nie będzie można już cofnąć."
        onCancel={() => setInvoiceConfirmationDialog(null)}
        onConfirm={() => handleDelete(invoiceConfirmationDialog!)}
      />
      <ConfirmationDialog
        open={!!invoiceDeleteAllDialog}
        title="Usuń fakturę z wielu kosztów"
        content="Ta faktura jest powiązana z wieloma kosztami. Czy usunąć ją ze wszystkich?"
        onCancel={() => setInvoiceDeleteAllDialog(null)}
        onConfirm={() => handleDeleteAll(invoiceDeleteAllDialog!)}
      />
      <InvoiceStatusDialog
        open={!!invoiceStatusDialog}
        status={invoices.find((i) => i.invoiceNumber === invoiceStatusDialog)?.status ?? "FOR_PAYMENT"}
        onChange={async (s) => {
          await onUpdateStatus(invoiceStatusDialog!, s);
          setInvoiceStatusDialog(null);
        }}
        onCancel={() => setInvoiceStatusDialog(null)}
      />
    </>
  );
};

export interface NewInvoiceData {
  readonly invoiceNumber: string;
  readonly file?: File;
  readonly date: string;
  readonly amount?: number;
  readonly status: CostInvoiceStatus;
  readonly exchangeRate?: number;
}

interface AddInvoiceProps {
  readonly invoice: NewInvoiceData;
  readonly cost: CostResponse;
  readonly file?: File;
  onChange: (change: Partial<NewInvoiceData>) => void;
  onChangeCostCurrency: (currency: CostCurrency) => Promise<void>;
}

const AddInvoice: FC<AddInvoiceProps> = ({ invoice, cost, onChange, onChangeCostCurrency, file: droppedFile }) => {
  const { customer } = useCustomerWithId(cost.order.customerId);
  const { products } = useProducts();
  const costForPdfDialog = useMemo(
    () =>
      customer && products
        ? {
            ...cost,
            order: {
              ...cost.order,
              products: cost.order.productIds.map((id) => ({
                id,
                name: products.find((p) => p.id === id)?.name ?? "",
              })),
            },
            customer: customer,
          }
        : undefined,
    [cost, customer, products],
  );
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [file, setFile] = useState<File>();
  const showExchangeRate = cost.currency && cost.currency !== cost.order.currency;
  const inputSize = showExchangeRate ? 2 : 3;
  const onClose = useCallback(() => setPdfDialogOpen(false), []);
  const handleFile = useCallback((file: File) => {
    onChange({ file });
    setFile(file);
    setPdfDialogOpen(!!file);
  }, []);
  useEffect(() => {
    if (droppedFile) handleFile(droppedFile);
  }, [droppedFile]);

  return (
    <Grid container spacing={1}>
      <Grid size={2}>
        <TextField
          label="Numer faktury"
          fullWidth
          value={invoice.invoiceNumber}
          onChange={(e) => onChange({ invoiceNumber: e.target.value })}
        />
      </Grid>
      <Grid size={2}>
        <DatePicker label="Data faktury" fullWidth value={invoice.date} onChange={(date) => onChange({ date })} />
      </Grid>
      <Grid size={2}>
        <InvoiceStatusSelect value={invoice.status} onChange={(status) => onChange({ status })} />
      </Grid>
      <Grid size={inputSize}>
        <TextField
          label="Kwota"
          value={invoice.amount ?? ""}
          fullWidth
          autoFocus
          type="number"
          onChange={(e) => onChange({ amount: Number(e.target.value) })}
          slotProps={{
            htmlInput: {
              step: 0.01,
            },
          }}
        />
      </Grid>
      {showExchangeRate && (
        <Grid size={inputSize}>
          <CostExchangeRate
            sourceCurrency={cost.currency}
            targetCurrency={cost.order.currency}
            date={invoiceDateToExchangeRateDate(invoice.date)}
            value={invoice.exchangeRate ?? 0}
            onChange={(exchangeRate) => onChange({ exchangeRate: exchangeRate ?? undefined })}
          />
        </Grid>
      )}
      <Grid size={inputSize}>
        <LocalFilePicker label="Faktura" fullWith onFileChange={handleFile} />
        {costForPdfDialog && (
          <PdfExtractDialog
            file={file}
            open={pdfDialogOpen}
            onDetect={onChange}
            onChangeCurrency={onChangeCostCurrency}
            onClose={onClose}
            cost={costForPdfDialog}
          />
        )}
      </Grid>
    </Grid>
  );
};

interface Props {
  cost: CostResponse;
  onAddInvoice: (invoice: AddInvoiceRequest) => Promise<InvoiceResponse>;
  onUpdateInvoiceStatus: (invoiceNumber: string, status: CostInvoiceStatus) => Promise<void>;
  onDeleteInvoice: (invoiceNumber: string, fromAll: boolean) => Promise<void>;
  onUpdateInvoicesExchangeRate: () => Promise<void>;
  onChangeCostCurrency: (currency: CostCurrency) => Promise<void>;
}

const InvoicesCard: FC<Props> = ({
  cost,
  onAddInvoice,
  onUpdateInvoiceStatus,
  onDeleteInvoice,
  onUpdateInvoicesExchangeRate,
  onChangeCostCurrency,
}) => {
  const { invoices } = cost;
  const orderPrice = cost.order.price;
  const orderCurrency = cost.order.currency;
  const [invoice, setInvoice] = useState<NewInvoiceData>({
    invoiceNumber: "",
    date: dayjs().format(ISO_DATE_FORMAT),
    amount: undefined,
    file: undefined,
    exchangeRate: undefined,
    status: "FOR_PAYMENT",
  });
  const addDisabled = !cost.currency || !invoice.invoiceNumber || !invoice.file || !invoice.date || !invoice.amount;
  const allInvoices = invoice.amount
    ? [
        ...invoices,
        {
          invoiceNumber: invoice.invoiceNumber,
          date: invoice.date,
          amount: invoice.amount,
          exchangeRate: invoice.exchangeRate,
          file: invoice.file,
          status: "FOR_PAYMENT",
        },
      ]
    : invoices;
  const showMargin = !!orderPrice && allInvoices.some((c) => !!c.amount);
  const handleSubmit = async () => {
    await onAddInvoice({
      invoiceNumber: invoice.invoiceNumber,
      date: invoice.date,
      amount: invoice.amount!,
      exchangeRate: invoice.exchangeRate,
      file: invoice.file!,
      status: invoice.status,
    });
    setInvoice({
      invoiceNumber: "",
      date: dayjs().format(ISO_DATE_FORMAT),
      amount: undefined,
      file: undefined,
      exchangeRate: undefined,
      status: "FOR_PAYMENT",
    });
  };
  const [pdfFile, setPdfFile] = useState<File | undefined>(undefined);
  const { isDragging, ...dndHandlers } = useDragAndDrop((dataTransfer) => {
    const files = Array.from(dataTransfer.files);
    const pdf = files.find((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (pdf) {
      setPdfFile(pdf);
    }
  });

  return (
    <Card style={{ backgroundColor: isDragging ? "lightblue" : undefined }} {...dndHandlers}>
      <CardHeader title="Faktury" />
      <CardContent>
        <Stack spacing={2}>
          <Invoices
            invoices={invoices}
            costCurrency={cost.currency}
            orderCurrency={orderCurrency}
            onUpdateStatus={onUpdateInvoiceStatus}
            onDelete={onDeleteInvoice}
          />
          <AddInvoice
            invoice={invoice}
            cost={cost}
            file={pdfFile}
            onChange={(v) => setInvoice((i) => ({ ...i, ...v }))}
            onChangeCostCurrency={onChangeCostCurrency}
          />
        </Stack>
      </CardContent>
      <CardActions>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            marginLeft: 2,
            flexGrow: 1,
          }}
        >
          {showMargin && (
            <Margin
              price={orderPrice}
              orderCurrency={orderCurrency}
              costCurrency={cost.currency}
              costInvoices={allInvoices}
            />
          )}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "flex-end",
              flexGrow: 1,
            }}
          >
            {invoices.length > 0 && (
              <AsyncLoadingButton color="secondary" onClick={onUpdateInvoicesExchangeRate}>
                Przelicz kurs
              </AsyncLoadingButton>
            )}
            <CancelButton
              onClick={() =>
                setInvoice({
                  invoiceNumber: "",
                  file: undefined,
                  date: dayjs().format(ISO_DATE_FORMAT),
                  amount: undefined,
                  exchangeRate: undefined,
                  status: "FOR_PAYMENT",
                })
              }
            />
            <AsyncLoadingButton disabled={addDisabled} onClick={handleSubmit}>
              Dodaj
            </AsyncLoadingButton>
          </Stack>
        </Stack>
      </CardActions>
    </Card>
  );
};

export default InvoicesCard;
